import React, { useEffect } from 'react';
import FormModal from '../../components/Modals/FormModal';
import SelectField from '../../components/Forms/Input/SelectField';

import { useEntityType } from '../../hooks/useEntityType';
import { useTaxpayerCategory } from '../../hooks/useTaxpayerCategory';
import { useEconomicActivities } from '../../hooks/useEconomicActivities';
import { useLocation } from '../../hooks/useLocation';

import { useDocumentIdentificationType } from '../../hooks/documentIdentificationType/useDocumentIdentificationType';
import SupplierNaturalForm from './forms/SupplierNaturalForm';
import SupplierJuridicoForm from './forms/SupplierJuridicoForm';
import SupplierSubjectExcludedForm from './forms/SupplierSubjectExcludedForm';
import { useSupplier } from '../../hooks/useSupplier';
import SupplierFormPlaceholder from './SupplierFormPlaceholder';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: () => void;
}

const SupplierRegistrationPopup: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { form, isSubmitting, updateFormField, resetForm, changeEntityType } =
    useSupplier();

  // Dropdowns
  const { fetchEntityTypeDropdown, entityTypeDropdown } = useEntityType();
  const { fetchTaxpayerCategoryDropdown, taxpayerCategoryDropdown } =
    useTaxpayerCategory();
  const {
    handlerAddEconomicActivity,
    fetchEconomicActivitiesDropdown,
    reloadEconomicActivitiesDropdown,
    economicActivitiesDropdown,
  } = useEconomicActivities();
  const {
    fetchDocumentIdentificationTypeDropdown,
    documentIdentificationTypeDropdown,
  } = useDocumentIdentificationType();

  const {
    fetchDepartments,
    fetchMunicipalitiesByDepartment,
    departments,
    municipalities,
  } = useLocation();

  // Al abrir modal: limpiar form y cargar dropdowns
  useEffect(() => {
    if (isOpen) {
      resetForm();
      fetchEntityTypeDropdown();
      fetchTaxpayerCategoryDropdown();
      fetchEconomicActivitiesDropdown();
      fetchDepartments();
      fetchDocumentIdentificationTypeDropdown();
    }
  }, [isOpen]);

  const entityTypeId = Number(form.entityTypeId);

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Registrar Proveedor"
      maxWidth="max-w-6xl"
    >
      <div className="mt-4 space-y-8">
        {/* 🔹 Sección: Tipo de Entidad */}
        <div>
          <div className="mb-6 border-b pb-4">
            <h2 className="text-gray-800 dark:text-gray-200 mb-1 text-xl font-bold">
              Selecciona el Tipo de Entidad
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Esta selección define los campos que deberás completar a
              continuación. Al elegir el tipo de entidad correcto, el sistema
              mostrará automáticamente el formulario específico para registrar
              al proveedor.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <SelectField
              iconKey="list"
              label="Tipo de Entidad"
              name="entityTypeId"
              value={form.entityTypeId}
              onChange={(e) => changeEntityType(Number(e.target.value))}
              options={entityTypeDropdown
                .filter((t) => ![3, 5].includes(t.id))
                .map((t) => ({
                  label: t.name,
                  value: t.id,
                }))}
            />
          </div>
        </div>

        {/* 🔹 Sección dinámica según tipo */}
        {entityTypeId ? (
          <div>
            <div className="mb-6 border-b pb-4">
              <h2 className="text-gray-800 dark:text-gray-200 mb-1 text-xl font-bold">
                Datos del Proveedor
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Completa la información requerida según el tipo de proveedor
                seleccionado.
              </p>
            </div>

            {entityTypeId === 1 && (
              <SupplierNaturalForm
                form={form}
                updateField={updateFormField}
                taxpayerCategoryDropdown={taxpayerCategoryDropdown}
                economicActivitiesDropdown={economicActivitiesDropdown}
                departments={departments}
                municipalities={municipalities}
                documentIdentificationTypeDropdown={
                  documentIdentificationTypeDropdown
                }
                onFetchMunicipalities={fetchMunicipalitiesByDepartment}
                onCancel={onClose}
                onSubmit={onSubmit}
                onEconomicActivityCreated={handlerAddEconomicActivity}
                onReloadEconomicActivities={reloadEconomicActivitiesDropdown}
                isSubmitting={isSubmitting}
              />
            )}

            {entityTypeId === 2 && (
              <SupplierJuridicoForm
                form={form}
                updateField={updateFormField}
                taxpayerCategoryDropdown={taxpayerCategoryDropdown}
                economicActivitiesDropdown={economicActivitiesDropdown}
                departments={departments}
                municipalities={municipalities}
                documentIdentificationTypeDropdown={
                  documentIdentificationTypeDropdown
                }
                onFetchMunicipalities={fetchMunicipalitiesByDepartment}
                onCancel={onClose}
                onSubmit={onSubmit}
                onEconomicActivityCreated={handlerAddEconomicActivity}
                onReloadEconomicActivities={reloadEconomicActivitiesDropdown}
                isSubmitting={isSubmitting}
              />
            )}

            {entityTypeId === 4 && (
              <SupplierSubjectExcludedForm
                form={form}
                updateField={updateFormField}
                departments={departments}
                municipalities={municipalities}
                documentIdentificationTypeDropdown={
                  documentIdentificationTypeDropdown
                }
                onFetchMunicipalities={fetchMunicipalitiesByDepartment}
                onCancel={onClose}
                onSubmit={onSubmit}
                isSubmitting={isSubmitting}
              />
            )}
          </div>
        ) : (
          <SupplierFormPlaceholder />
        )}
      </div>
    </FormModal>
  );
};

export default SupplierRegistrationPopup;
